import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, VStack, Text, Button, Alert, AlertIcon } from '@chakra-ui/react'
import { FaLock, FaCreditCard } from 'react-icons/fa'
import { usePaywallContext } from '@/contexts/PaywallContext'

interface TrialBlockingGuardProps {
  children: React.ReactNode
}

export const TrialBlockingGuard: React.FC<TrialBlockingGuardProps> = ({ children }) => {
  const { isTrialExpired, isSubscriptionActive, isLoading } = usePaywallContext()
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="200px"
      >
        <Text color="white">Loading...</Text>
      </Box>
    )
  }

  // Only block if trial is expired AND no active subscription (paid or trial)
  if (isTrialExpired && !isSubscriptionActive) {
    return (
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="#09090b"
        zIndex={9999}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Box
          maxW="md"
          w="full"
          bg="#191919"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.700"
          p={8}
        >
          <VStack spacing={6} textAlign="center">
            <Box color="red.400" fontSize="4xl">
              <FaLock />
            </Box>
            
            <VStack spacing={3}>
              <Text size="lg" color="white">
                Trial / Subscription Expired
              </Text>
              <Text color="gray.400">
                To continue using the scoring features, please subscribe to a plan.
              </Text>
            </VStack>

            <Alert
              status="info"
              borderRadius="md"
              bg="blue.900"
              border="1px solid"
              borderColor="blue.700"
            >
              <AlertIcon color="blue.400" />
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="medium" color="blue.100">
                  Subscription Required
                </Text>
                <Text fontSize="xs" color="blue.200">
                  Choose a plan to unlock unlimited scoring and all features.
                </Text>
              </VStack>
            </Alert>

            <VStack spacing={3} w="full">
              <Button
                leftIcon={<FaCreditCard />}
                colorScheme="blue"
                size="lg"
                w="full"
                onClick={() => navigate('/pricing')}
              >
                View Plans & Subscribe
              </Button>
              <Button
                variant="ghost"
                color="gray.400"
                w="full"
                onClick={() => navigate('/')}
              >
                Back to Home
              </Button>
            </VStack>
          </VStack>
        </Box>
      </Box>
    )
  }

  return <>{children}</>
} 